/**
 * Quiz Game Engine - Bernard Miniatures
 * 4 modes: Quiz, Find the Outcast, Guess the Price, Speed Round
 */
(function () {
  'use strict';

  /* ============================================
     TAG PARSER
     Converts raw Shopify tags into structured categories
     ============================================ */
  const TAG_RULES = [
    { pattern: /^années-(\d{4})$/, category: 'decade', extract: (m) => m[1] + 's' },
    { pattern: /^époques--(.+)$/, category: 'epoch', extract: (m) => formatTag(m[1]) },
    { pattern: /^voitures-francaise$/, category: 'nationality', value: 'Française' },
    { pattern: /^voitures-etrangères--(.+)$/, category: 'nationality', extract: (m) => formatTag(m[1]) },
    { pattern: /^voitures--(.+)$/, category: 'body_style', extract: (m) => formatTag(m[1]) },
    { pattern: /^camions--(.+)$/, category: 'body_style', extract: (m) => 'Camion ' + formatTag(m[1]) },
    { pattern: /^utilitaires--(.+)$/, category: 'body_style', extract: (m) => formatTag(m[1]) },
    { pattern: /^sport--competition--(.+)$/, category: 'competition', extract: (m) => formatTag(m[1]) },
    { pattern: /^modèles-populaires--(.+)$/, category: 'popular_model', extract: (m) => formatTag(m[1]) },
  ];

  function formatTag(str) {
    return str.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function parseTags(tags) {
    const parsed = {};
    for (const tag of tags) {
      const lower = tag.toLowerCase();
      for (const rule of TAG_RULES) {
        const match = lower.match(rule.pattern);
        if (match) {
          const cat = rule.category;
          const val = rule.value || rule.extract(match);
          if (!parsed[cat]) parsed[cat] = [];
          if (!parsed[cat].includes(val)) parsed[cat].push(val);
        }
      }
    }
    return parsed;
  }

  /* ============================================
     QUESTION TYPES
     ============================================ */
  const QUESTION_TYPES = {
    brand: {
      question: 'Quelle est la marque ?',
      getAnswer: (p) => p.vendor,
      getPool: (products) => unique(products.map((p) => p.vendor)),
    },
    decade: {
      question: 'De quelle decennie date ce vehicule ?',
      getAnswer: (p) => (p.parsed_tags.decade || [])[0],
      getPool: (products) => unique(products.flatMap((p) => p.parsed_tags.decade || [])),
    },
    nationality: {
      question: 'Quel est le pays d\'origine ?',
      getAnswer: (p) => (p.parsed_tags.nationality || [])[0],
      getPool: (products) => unique(products.flatMap((p) => p.parsed_tags.nationality || [])),
    },
    body_style: {
      question: 'Quel type de carrosserie ?',
      getAnswer: (p) => (p.parsed_tags.body_style || [])[0],
      getPool: (products) => unique(products.flatMap((p) => p.parsed_tags.body_style || [])),
    },
    manufacturer: {
      question: 'Quel est le fabricant de cette miniature ?',
      getAnswer: (p) => p.manufacturer,
      getPool: (products) => unique(products.map((p) => p.manufacturer).filter(Boolean)),
    },
    scale: {
      question: 'Quelle est l\'echelle ?',
      getAnswer: (p) => p.scale,
      getPool: (products) => unique(products.map((p) => p.scale).filter(Boolean)),
    },
    type: {
      question: 'Voiture ou camion ?',
      getAnswer: (p) => p.type,
      getPool: (products) => unique(products.map((p) => p.type)),
    },
  };

  const DIFFICULTY_WEIGHTS = {
    easy: ['brand', 'type', 'nationality'],
    medium: ['brand', 'decade', 'nationality', 'body_style', 'manufacturer'],
    hard: ['decade', 'body_style', 'manufacturer', 'scale', 'brand', 'nationality'],
  };

  /* ============================================
     PRICE RANGES
     ============================================ */
  function getPriceRanges(correctPrice) {
    const price = correctPrice / 100;
    const ranges = [];
    const correctRange = priceToRange(price);
    ranges.push(correctRange);

    const allRanges = [
      { label: 'Moins de 10\u20AC', min: 0, max: 10 },
      { label: '10\u20AC - 15\u20AC', min: 10, max: 15 },
      { label: '15\u20AC - 20\u20AC', min: 15, max: 20 },
      { label: '20\u20AC - 25\u20AC', min: 20, max: 25 },
      { label: '25\u20AC - 35\u20AC', min: 25, max: 35 },
      { label: '35\u20AC - 50\u20AC', min: 35, max: 50 },
      { label: 'Plus de 50\u20AC', min: 50, max: 999 },
    ];

    const others = allRanges.filter((r) => r.label !== correctRange);
    shuffle(others);
    ranges.push(...others.slice(0, 3).map((r) => r.label));

    return { correct: correctRange, options: shuffle(ranges) };
  }

  function priceToRange(price) {
    if (price < 10) return 'Moins de 10\u20AC';
    if (price < 15) return '10\u20AC - 15\u20AC';
    if (price < 20) return '15\u20AC - 20\u20AC';
    if (price < 25) return '20\u20AC - 25\u20AC';
    if (price < 35) return '25\u20AC - 35\u20AC';
    if (price < 50) return '35\u20AC - 50\u20AC';
    return 'Plus de 50\u20AC';
  }

  /* ============================================
     UTILITIES
     ============================================ */
  function shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function unique(arr) {
    return [...new Set(arr.filter(Boolean))];
  }

  function pickRandom(arr, count) {
    const shuffled = shuffle([...arr]);
    return shuffled.slice(0, count);
  }

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function $$(sel, ctx) {
    return [...(ctx || document).querySelectorAll(sel)];
  }

  /* ============================================
     GAME ENGINE
     ============================================ */
  class QuizGame {
    constructor(el) {
      this.el = el;
      this.config = {
        collection: el.dataset.collection || 'all',
        timerSeconds: parseInt(el.dataset.timer) || 15,
        speedTimerSeconds: parseInt(el.dataset.speedTimer) || 5,
        questionsPerRound: parseInt(el.dataset.questionsPerRound) || 10,
        difficulty: el.dataset.difficulty || 'medium',
      };

      this.state = {
        mode: null,
        screen: 'mode-select',
        products: [],
        questions: [],
        currentQuestion: 0,
        score: 0,
        streak: 0,
        bestStreak: 0,
        correctCount: 0,
        timerStart: 0,
        timerDuration: 0,
        timerRaf: null,
        answered: false,
      };

      this.screens = {};
      $$('[data-screen]', el).forEach((s) => {
        this.screens[s.dataset.screen] = s;
      });

      this.bindEvents();
    }

    /* --- Event binding --- */
    bindEvents() {
      // Mode selection
      $$('.quiz-mode-card', this.el).forEach((btn) => {
        btn.addEventListener('click', () => this.startGame(btn.dataset.mode));
      });

      // Quiz answers
      $$('.quiz-answer', this.el).forEach((btn) => {
        btn.addEventListener('click', () => this.handleAnswer(btn));
      });

      // Outcast cards
      $$('.quiz-outcast-card', this.el).forEach((btn) => {
        btn.addEventListener('click', () => this.handleOutcastPick(btn));
      });

      // End screen actions
      $('[data-action="replay"]', this.el).addEventListener('click', () => {
        this.startGame(this.state.mode);
      });
      $('[data-action="menu"]', this.el).addEventListener('click', () => {
        this.showScreen('mode-select');
      });
    }

    /* --- Screen management --- */
    showScreen(name) {
      Object.values(this.screens).forEach((s) => s.classList.remove('active'));
      this.screens[name].classList.add('active');
      this.state.screen = name;
    }

    /* --- Data loading --- */
    async loadProducts() {
      this.showScreen('loading');

      const randomOffset = Math.floor(Math.random() * 200);
      const url = `${window.theme.routes.root_url}?section_id=quiz-game-data&collection=${this.config.collection}&offset=${randomOffset}&limit=50`;

      try {
        const resp = await fetch(url);
        const text = await resp.text();
        // Section rendering API wraps in a div, extract JSON
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error('No JSON found');
        const data = JSON.parse(jsonMatch[0]);

        // Parse tags for each product
        data.products.forEach((p) => {
          p.parsed_tags = parseTags(p.tags);
        });

        this.state.products = data.products;

        // If we got too few products, try offset 0
        if (data.products.length < 10 && randomOffset > 0) {
          const url2 = `${window.theme.routes.root_url}?section_id=quiz-game-data&collection=${this.config.collection}&offset=0&limit=50`;
          const resp2 = await fetch(url2);
          const text2 = await resp2.text();
          const json2 = text2.match(/\{[\s\S]*\}/);
          if (json2) {
            const data2 = JSON.parse(json2[0]);
            data2.products.forEach((p) => {
              p.parsed_tags = parseTags(p.tags);
            });
            // Merge unique products
            const existingIds = new Set(this.state.products.map((p) => p.id));
            for (const p of data2.products) {
              if (!existingIds.has(p.id)) {
                this.state.products.push(p);
              }
            }
          }
        }

        return this.state.products.length >= 4;
      } catch (err) {
        console.error('Quiz: Failed to load products', err);
        return false;
      }
    }

    /* --- Game start --- */
    async startGame(mode) {
      this.state.mode = mode;
      this.state.score = 0;
      this.state.streak = 0;
      this.state.bestStreak = 0;
      this.state.correctCount = 0;
      this.state.currentQuestion = 0;
      this.state.answered = false;

      if (this.state.products.length < 4) {
        const ok = await this.loadProducts();
        if (!ok) {
          alert('Pas assez de miniatures disponibles pour jouer.');
          this.showScreen('mode-select');
          return;
        }
      }

      this.generateQuestions();
      this.showNextQuestion();
    }

    /* --- Question generation --- */
    generateQuestions() {
      const products = shuffle([...this.state.products]);
      const count = this.config.questionsPerRound;
      this.state.questions = [];

      if (this.state.mode === 'outcast') {
        this.generateOutcastQuestions(products, count);
      } else if (this.state.mode === 'price') {
        this.generatePriceQuestions(products, count);
      } else if (this.state.mode === 'speed') {
        this.generateQuizQuestions(products, count, ['brand']);
      } else {
        const types = DIFFICULTY_WEIGHTS[this.config.difficulty] || DIFFICULTY_WEIGHTS.medium;
        this.generateQuizQuestions(products, count, types);
      }
    }

    generateQuizQuestions(products, count, allowedTypes) {
      for (let i = 0; i < count && i < products.length; i++) {
        const product = products[i];
        const typeKeys = shuffle([...allowedTypes]);

        let question = null;
        for (const key of typeKeys) {
          const type = QUESTION_TYPES[key];
          const answer = type.getAnswer(product);
          if (!answer) continue;

          const pool = type.getPool(this.state.products).filter((v) => v !== answer);
          if (pool.length < 3) continue;

          const wrongAnswers = pickRandom(pool, 3);
          const options = shuffle([answer, ...wrongAnswers]);

          question = {
            type: 'quiz',
            questionType: key,
            product,
            text: type.question,
            options,
            correctAnswer: answer,
          };
          break;
        }

        if (question) {
          this.state.questions.push(question);
        }
      }
    }

    generatePriceQuestions(products, count) {
      for (let i = 0; i < count && i < products.length; i++) {
        const product = products[i];
        const { correct, options } = getPriceRanges(product.price);

        this.state.questions.push({
          type: 'price',
          product,
          text: 'Devinez le prix de cette miniature',
          options,
          correctAnswer: correct,
        });
      }
    }

    generateOutcastQuestions(products, count) {
      // Group products by tags
      const tagGroups = {};
      for (const p of products) {
        for (const [cat, vals] of Object.entries(p.parsed_tags)) {
          for (const val of vals) {
            const key = cat + '::' + val;
            if (!tagGroups[key]) tagGroups[key] = [];
            tagGroups[key].push(p);
          }
        }
      }

      // Also group by vendor
      const vendorGroups = {};
      for (const p of products) {
        if (!vendorGroups[p.vendor]) vendorGroups[p.vendor] = [];
        vendorGroups[p.vendor].push(p);
      }

      // Find groups with 5+ products
      const validGroups = Object.entries(tagGroups)
        .filter(([, prods]) => prods.length >= 5)
        .map(([key, prods]) => {
          const [cat, val] = key.split('::');
          return { category: cat, value: val, products: prods };
        });

      shuffle(validGroups);

      for (let i = 0; i < count && i < validGroups.length; i++) {
        const group = validGroups[i];
        const groupProducts = pickRandom(group.products, 5);
        const groupIds = new Set(groupProducts.map((p) => p.id));

        // Find an outcast - a product NOT in this group
        const outcasts = products.filter(
          (p) => !groupIds.has(p.id) && !(p.parsed_tags[group.category] || []).includes(group.value)
        );

        if (outcasts.length === 0) continue;

        const outcast = outcasts[Math.floor(Math.random() * outcasts.length)];
        const allCards = shuffle([...groupProducts, outcast]);
        const outcastIndex = allCards.findIndex((p) => p.id === outcast.id);

        let hintText = '';
        switch (group.category) {
          case 'decade':
            hintText = `5 voitures des ${group.value}, 1 intrus`;
            break;
          case 'nationality':
            hintText = `5 voitures ${group.value.toLowerCase()}s, 1 intrus`;
            break;
          case 'body_style':
            hintText = `5 ${group.value.toLowerCase()}, 1 intrus`;
            break;
          case 'competition':
            hintText = `5 voitures de ${group.value}, 1 intrus`;
            break;
          default:
            hintText = `Trouvez la voiture qui n'appartient pas au groupe`;
        }

        this.state.questions.push({
          type: 'outcast',
          cards: allCards,
          outcastIndex,
          outcast,
          groupCategory: group.category,
          groupValue: group.value,
          hint: hintText,
        });
      }
    }

    /* --- Show next question --- */
    showNextQuestion() {
      if (this.state.currentQuestion >= this.state.questions.length) {
        this.showEndScreen();
        return;
      }

      const q = this.state.questions[this.state.currentQuestion];
      this.state.answered = false;

      if (q.type === 'outcast') {
        this.showOutcastQuestion(q);
      } else {
        this.showQuizQuestion(q);
      }
    }

    showQuizQuestion(q) {
      this.showScreen('question');
      const screen = this.screens.question;

      // Update HUD
      this.updateHUD(screen);

      // Image
      const img = $('.quiz-image', screen);
      img.src = q.product.clean_image;
      img.alt = 'Miniature';

      // Question text
      $('.quiz-question-text', screen).textContent = q.text;

      // Answers
      $$('.quiz-answer', screen).forEach((btn, i) => {
        btn.textContent = q.options[i];
        btn.className = 'quiz-answer';
        btn.disabled = false;
      });

      // Timer
      const duration = this.state.mode === 'speed' ? this.config.speedTimerSeconds : this.config.timerSeconds;
      this.startTimer(duration, screen);
    }

    showOutcastQuestion(q) {
      this.showScreen('outcast');
      const screen = this.screens.outcast;

      // Update HUD
      this.updateHUD(screen);

      // Hint
      $('.quiz-outcast-hint', screen).textContent = q.hint;

      // Cards
      $$('.quiz-outcast-card', screen).forEach((card, i) => {
        const img = card.querySelector('img');
        if (q.cards[i]) {
          img.src = q.cards[i].clean_image;
          img.alt = q.cards[i].title;
          card.style.display = '';
          card.className = 'quiz-outcast-card';
          card.disabled = false;
        } else {
          card.style.display = 'none';
        }
      });

      // Timer
      this.startTimer(this.config.timerSeconds, screen);
    }

    updateHUD(screen) {
      const q = this.state;
      $$('.quiz-score-value', screen).forEach((el) => (el.textContent = q.score));
      $$('.quiz-progress-text', screen).forEach(
        (el) => (el.textContent = `${q.currentQuestion + 1} / ${q.questions.length}`)
      );
      $$('.quiz-streak-value', screen).forEach((el) => (el.textContent = q.streak));
    }

    /* --- Timer --- */
    startTimer(seconds, screen) {
      this.stopTimer();

      this.state.timerStart = performance.now();
      this.state.timerDuration = seconds * 1000;

      const fill = $('.quiz-timer-fill', screen);
      const text = $('.quiz-timer-text', screen);

      fill.classList.remove('urgent');
      text.classList.remove('urgent');

      const tick = () => {
        const elapsed = performance.now() - this.state.timerStart;
        const remaining = Math.max(0, this.state.timerDuration - elapsed);
        const pct = remaining / this.state.timerDuration;

        fill.style.width = (pct * 100) + '%';
        text.textContent = Math.ceil(remaining / 1000);

        if (pct < 0.25) {
          fill.classList.add('urgent');
          text.classList.add('urgent');
        }

        if (remaining <= 0) {
          this.handleTimeout();
          return;
        }

        this.state.timerRaf = requestAnimationFrame(tick);
      };

      this.state.timerRaf = requestAnimationFrame(tick);
    }

    stopTimer() {
      if (this.state.timerRaf) {
        cancelAnimationFrame(this.state.timerRaf);
        this.state.timerRaf = null;
      }
    }

    /* --- Answer handling --- */
    handleAnswer(btn) {
      if (this.state.answered) return;
      this.state.answered = true;
      this.stopTimer();

      const q = this.state.questions[this.state.currentQuestion];
      const selected = btn.textContent;
      const isCorrect = selected === q.correctAnswer;

      // Visual feedback on answers
      $$('.quiz-answer', this.screens.question).forEach((b) => {
        b.disabled = true;
        if (b.textContent === q.correctAnswer) b.classList.add('correct');
        if (b === btn && !isCorrect) b.classList.add('wrong');
      });

      this.processResult(isCorrect, q.product);
    }

    handleOutcastPick(card) {
      if (this.state.answered) return;
      this.state.answered = true;
      this.stopTimer();

      const q = this.state.questions[this.state.currentQuestion];
      const idx = parseInt(card.dataset.index);
      const isCorrect = idx === q.outcastIndex;

      // Visual feedback
      $$('.quiz-outcast-card', this.screens.outcast).forEach((c, i) => {
        c.disabled = true;
        if (i === q.outcastIndex) c.classList.add('correct');
        if (c === card && !isCorrect) c.classList.add('wrong');
      });

      this.processResult(isCorrect, q.outcast);
    }

    handleTimeout() {
      if (this.state.answered) return;
      this.state.answered = true;
      this.stopTimer();

      const q = this.state.questions[this.state.currentQuestion];

      if (q.type === 'outcast') {
        $$('.quiz-outcast-card', this.screens.outcast).forEach((c, i) => {
          c.disabled = true;
          if (i === q.outcastIndex) c.classList.add('reveal');
        });
        this.showFeedback(false, q.outcast, true);
      } else {
        $$('.quiz-answer', this.screens.question).forEach((b) => {
          b.disabled = true;
          if (b.textContent === q.correctAnswer) b.classList.add('correct');
        });
        this.showFeedback(false, q.product, true);
      }
    }

    processResult(isCorrect, product) {
      if (isCorrect) {
        // Calculate points: base + speed bonus + streak bonus
        const elapsed = performance.now() - this.state.timerStart;
        const pctLeft = Math.max(0, 1 - elapsed / this.state.timerDuration);
        const speedBonus = Math.round(pctLeft * 5);
        const streakBonus = Math.min(this.state.streak, 5);
        const points = 10 + speedBonus + streakBonus;

        this.state.score += points;
        this.state.streak += 1;
        this.state.correctCount += 1;
        if (this.state.streak > this.state.bestStreak) {
          this.state.bestStreak = this.state.streak;
        }

        this.showPointsPopup('+' + points);

        if (this.state.streak >= 3) {
          this.showStreakIndicator(this.state.streak);
        }
      } else {
        this.state.streak = 0;
      }

      this.showFeedback(isCorrect, product, false);
    }

    /* --- Feedback --- */
    showFeedback(isCorrect, product, isTimeout) {
      setTimeout(() => {
        this.showScreen('feedback');
        const screen = this.screens.feedback;

        const icon = $('.quiz-feedback-icon', screen);
        const title = $('.quiz-feedback-title', screen);
        const detail = $('.quiz-feedback-detail', screen);
        const img = $('.quiz-feedback-image', screen);
        const name = $('.quiz-feedback-name', screen);
        const meta = $('.quiz-feedback-meta', screen);

        icon.className = 'quiz-feedback-icon';

        if (isTimeout) {
          icon.classList.add('timeout');
          icon.textContent = '\u23F0';
          title.textContent = 'Temps ecoule !';
          title.style.color = '';
        } else if (isCorrect) {
          icon.classList.add('correct');
          icon.textContent = '\u2713';
          title.textContent = 'Bonne reponse !';
          title.style.color = 'var(--quiz-correct)';
        } else {
          icon.classList.add('wrong');
          icon.textContent = '\u2717';
          title.textContent = 'Mauvaise reponse';
          title.style.color = 'var(--quiz-wrong)';
        }

        // Show correct answer detail
        const q = this.state.questions[this.state.currentQuestion];
        if (q.type === 'outcast') {
          detail.textContent = `L'intrus etait : ${product.title}`;
        } else {
          detail.textContent = `La reponse etait : ${q.correctAnswer}`;
        }

        img.src = product.clean_image;
        name.textContent = product.title;
        meta.textContent = `${product.vendor} \u2022 ${product.scale || ''} \u2022 ${(product.price / 100).toFixed(0)}\u20AC`;

        // Auto-advance
        setTimeout(() => {
          this.state.currentQuestion++;
          this.showNextQuestion();
        }, 2000);
      }, 600);
    }

    /* --- Popups --- */
    showPointsPopup(text) {
      const popup = document.createElement('div');
      popup.className = 'quiz-points-popup';
      popup.textContent = text;
      this.el.appendChild(popup);
      setTimeout(() => popup.remove(), 800);
    }

    showStreakIndicator(streak) {
      // Remove existing
      const existing = this.el.querySelector('.quiz-streak-indicator');
      if (existing) existing.remove();

      const el = document.createElement('div');
      el.className = 'quiz-streak-indicator';
      el.textContent = `\uD83D\uDD25 ${streak} d'affilee !`;
      this.el.appendChild(el);
      setTimeout(() => el.remove(), 1500);
    }

    /* --- End screen --- */
    showEndScreen() {
      this.showScreen('end');
      const screen = this.screens.end;

      $('.quiz-final-score', screen).textContent = this.state.score;
      $('.quiz-final-correct', screen).textContent =
        this.state.correctCount + '/' + this.state.questions.length;
      $('.quiz-final-streak', screen).textContent = this.state.bestStreak;
    }
  }

  /* --- Init --- */
  function init() {
    const el = document.getElementById('quiz-game');
    if (!el) return;
    new QuizGame(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
